import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Users, Brain, MessageCircle, Clock, Zap, Phone, AlertTriangle, Globe, FileText, ArrowRight } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService, SystemStatus, TelephonyStatus, TranslationSummary, LanguageConfigResponse, DashboardMetrics, MetricType, TimeRange, MetricsHistoryPoint } from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';
import { MetricsChart } from '../MetricsChart';
import { AdminEventsTable } from '../AdminEventsTable';
import { ErrorEventsSummaryCards } from './error-events/ErrorEventsSummaryCards';

interface OverviewPageProps {
  /** Admin-Rolle des eingeloggten Users — gated die Error-Event-Cards (Summary-API ist Super-Admin-only). */
  userRole?: string;
}

export function OverviewPage({ userRole }: OverviewPageProps) {
  const navigate = useNavigate();
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const [telephonyStatus, setTelephonyStatus] = useState<TelephonyStatus | null>(null);
  const [translationSummary, setTranslationSummary] = useState<TranslationSummary | null>(null);
  const [languageConfig, setLanguageConfig] = useState<LanguageConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [telephonyLoading, setTelephonyLoading] = useState(true);
  const [translationLoading, setTranslationLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [telephonyError, setTelephonyError] = useState<string | null>(null);
  const [translationError, setTranslationError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [selectedMetric, setSelectedMetric] = useState<MetricType>('monthlyConversations');
  const [timeRange, setTimeRange] = useState<TimeRange>('day');
  const [historyData, setHistoryData] = useState<MetricsHistoryPoint[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSystemStatus = async () => {
      try {
        setLoading(true);
        const status = await apiService.getSystemStatus();
        setSystemStatus(status);
        setError(null);
      } catch (err: any) {
        console.error('Failed to fetch system status:', err);
        const errorMessage = extractErrorMessage(
          err,
          'Fehler beim Laden des Systemstatus.',
          'load-system-status'
        );
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchSystemStatus();

    // Refresh every 30 seconds
    const interval = setInterval(fetchSystemStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchTelephonyStatus = async () => {
      try {
        setTelephonyLoading(true);
        const status = await apiService.getTelephonyStatus();
        setTelephonyStatus(status);
        setTelephonyError(null);
      } catch (err: any) {
        console.error('Failed to fetch telephony status:', err);
        const errorMessage = extractErrorMessage(
          err,
          'Fehler beim Laden des Telefonie-Status.',
          'load-telephony-status'
        );
        setTelephonyError(errorMessage);
      } finally {
        setTelephonyLoading(false);
      }
    };

    fetchTelephonyStatus();

    // Refresh every 60 seconds
    const interval = setInterval(fetchTelephonyStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchTranslationStatus = async () => {
      try {
        setTranslationLoading(true);
        const [summary, config] = await Promise.all([
          apiService.getTranslationSummary(),
          apiService.getLanguageConfig(),
        ]);
        setTranslationSummary(summary);
        setLanguageConfig(config);
        setTranslationError(null);
      } catch (err: any) {
        console.error('Failed to fetch translation status:', err);
        const errorMessage = extractErrorMessage(
          err,
          'Fehler beim Laden des Uebersetzungsstatus.',
          'load-translation-status'
        );
        setTranslationError(errorMessage);
      } finally {
        setTranslationLoading(false);
      }
    };

    fetchTranslationStatus();

    // Refresh every 60 seconds
    const interval = setInterval(fetchTranslationStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setMetricsLoading(true);
        const data = await apiService.getDashboardMetrics();
        setMetrics(data);
        setMetricsError(null);
      } catch (err: any) {
        console.error('Failed to fetch dashboard metrics:', err);
        const errorMessage = extractErrorMessage(
          err,
          'Fehler beim Laden der Dashboard-Metriken.',
          'load-dashboard-metrics'
        );
        setMetricsError(errorMessage);
      } finally {
        setMetricsLoading(false);
      }
    };

    fetchMetrics();

    // Refresh every 30 seconds
    const interval = setInterval(fetchMetrics, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setHistoryLoading(true);
        const response = await apiService.getMetricsHistory(selectedMetric, timeRange);
        setHistoryData(response.data);
        setHistoryError(null);
      } catch (err: any) {
        console.error('Failed to fetch metrics history:', err);
        const errorMessage = extractErrorMessage(
          err,
          'Fehler beim Laden der Verlaufsdaten.',
          'load-metrics-history'
        );
        setHistoryError(errorMessage);
      } finally {
        setHistoryLoading(false);
      }
    };

    fetchHistory();
  }, [selectedMetric, timeRange]);

  const stats: { key: MetricType; title: string; value: string; icon: typeof Users; color: string; bgColor: string }[] = [
    {
      key: 'activeCustomers',
      title: "Aktive Kunden",
      value: metricsLoading ? "..." : metricsError ? "-" : (metrics?.activeCustomers?.toLocaleString() ?? "0"),
      icon: Users,
      color: "text-blue-600",
      bgColor: "bg-blue-50"
    },
    {
      key: 'monthlyConversations',
      title: "Monatliche Gespräche",
      value: metricsLoading ? "..." : metricsError ? "-" : (metrics?.monthlyConversations?.toLocaleString() ?? "0"),
      icon: MessageCircle,
      color: "text-green-600",
      bgColor: "bg-green-50"
    },
    {
      key: 'totalCallMinutes',
      title: "Gesamtminuten",
      value: metricsLoading ? "..." : metricsError ? "-" : (metrics?.totalCallMinutes?.toLocaleString() ?? "0"),
      icon: Clock,
      color: "text-orange-600",
      bgColor: "bg-orange-50"
    },
    {
      key: 'activeLlmModels',
      title: "Active LLM Models",
      value: metricsLoading ? "..." : metricsError ? "-" : (metrics?.activeLlmModels?.toLocaleString() ?? "0"),
      icon: Brain,
      color: "text-purple-600",
      bgColor: "bg-purple-50"
    },
    {
      key: 'apiCallsToday',
      title: "API Calls Today",
      value: metricsLoading ? "..." : metricsError ? "-" : (metrics?.apiCallsToday?.toLocaleString() ?? "0"),
      icon: Zap,
      color: "text-yellow-600",
      bgColor: "bg-yellow-50"
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard Übersicht</h1>
        <p className="text-gray-600">Willkommen im AI Platform Administration Interface</p>
      </div>

      {/* Error Events + DLQ — nur Super-Admins, die Summary-API ist Super-Admin-only. */}
      {userRole === 'super_admin' && <ErrorEventsSummaryCards />}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          const isSelected = selectedMetric === stat.key;
          return (
            <Card
              key={stat.key}
              className={`cursor-pointer transition-all hover:shadow-md ${
                isSelected ? 'ring-2 ring-offset-2 ring-blue-500' : ''
              }`}
              onClick={() => setSelectedMetric(stat.key)}
            >
              <CardContent className="p-6">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                    <Icon className={`w-5 h-5 ${stat.color}`} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">{stat.title}</p>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      {metricsError && (
        <p className="text-sm text-red-600 text-center">{metricsError}</p>
      )}
      {metrics?.lastUpdated && !metricsLoading && !metricsError && (
        <p className="text-xs text-gray-400 text-center">
          Zuletzt aktualisiert: {new Date(metrics.lastUpdated).toLocaleString('de-DE')}
        </p>
      )}

      {/* Time Range Filter and Chart */}
      <div className="flex justify-end">
        <div className="inline-flex rounded-md shadow-sm" role="group">
          {([
            { value: 'day', label: 'Tag' },
            { value: 'week', label: 'Woche' },
            { value: 'month', label: 'Monat' },
          ] as const).map((option, index, arr) => (
            <Button
              key={option.value}
              variant={timeRange === option.value ? 'default' : 'outline'}
              onClick={() => setTimeRange(option.value)}
              className={`${
                index === 0 ? 'rounded-r-none' : index === arr.length - 1 ? 'rounded-l-none' : 'rounded-none'
              } ${index !== 0 ? '-ml-px' : ''}`}
            >
              {option.label}
            </Button>
          ))}
        </div>
      </div>

      <MetricsChart
        data={historyData}
        metric={selectedMetric}
        range={timeRange}
        loading={historyLoading}
        error={historyError}
      />

      {/* Admin Events */}
      <div className="h-[33vh]">
        <AdminEventsTable />
      </div>

      {/* Telephony Status Card */}
      <Card className={telephonyStatus?.warning ? 'border-yellow-400 border-2' : ''}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${telephonyStatus?.warning ? 'bg-yellow-100' : 'bg-green-100'}`}>
                <Phone className={`w-5 h-5 ${telephonyStatus?.warning ? 'text-yellow-600' : 'text-green-600'}`} />
              </div>
              <div>
                <CardTitle className="text-lg">Telefonnummern Status</CardTitle>
                <CardDescription>Verfügbare CIDs für Voicebots</CardDescription>
              </div>
            </div>
            {telephonyStatus?.warning && (
              <div className="flex items-center space-x-2 text-yellow-600 bg-yellow-50 px-3 py-1.5 rounded-full">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">Warnung</span>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {telephonyLoading ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
            </div>
          ) : telephonyError ? (
            <div className="text-center py-4">
              <p className="text-red-600 text-sm">{telephonyError}</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-4xl font-bold ${telephonyStatus?.warning ? 'text-yellow-600' : 'text-green-600'}`}>
                    {telephonyStatus?.freeCids ?? 0}
                  </p>
                  <p className="text-sm text-gray-500">Freie Telefonnummern</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Warnschwelle</p>
                  <p className="text-lg font-medium text-gray-900">{telephonyStatus?.warningThreshold ?? 50}</p>
                </div>
              </div>
              {telephonyStatus?.warning && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">Geringe Anzahl verfügbarer Nummern</p>
                      <p className="text-sm text-yellow-700 mt-1">
                        Es sind weniger als {telephonyStatus.warningThreshold} Telefonnummern verfügbar.
                        Bitte kontaktieren Sie den Provider um neue Nummern zu bestellen.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <div className={`text-xs px-2 py-1 rounded inline-block ${
                telephonyStatus?.status === 'healthy' ? 'bg-green-100 text-green-700' :
                telephonyStatus?.status === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                'bg-red-100 text-red-700'
              }`}>
                Status: {telephonyStatus?.status === 'healthy' ? 'Gesund' :
                         telephonyStatus?.status === 'warning' ? 'Warnung' : 'Fehler'}
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Hinweis: Freigegebene Telefonnummern werden erst nach 30 Tagen wieder verfügbar (einstellbar via CID_COOLDOWN_DAYS im LiveKit-Backend).
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Translation Status Card */}
      <Card className={(translationSummary?.incompleteCount ?? 0) > 0 ? 'border-yellow-400 border-2' : ''}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${(translationSummary?.incompleteCount ?? 0) > 0 ? 'bg-yellow-100' : 'bg-green-100'}`}>
                <Globe className={`w-5 h-5 ${(translationSummary?.incompleteCount ?? 0) > 0 ? 'text-yellow-600' : 'text-green-600'}`} />
              </div>
              <div>
                <CardTitle className="text-lg">Uebersetzungsstatus</CardTitle>
                <CardDescription>Prompt Templates Uebersetzungen</CardDescription>
              </div>
            </div>
            {(translationSummary?.incompleteCount ?? 0) > 0 && (
              <div className="flex items-center space-x-2 text-yellow-600 bg-yellow-50 px-3 py-1.5 rounded-full">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">Unvollstaendig</span>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {translationLoading ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
            </div>
          ) : translationError ? (
            <div className="text-center py-4">
              <p className="text-red-600 text-sm">{translationError}</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-4xl font-bold ${(translationSummary?.incompleteCount ?? 0) > 0 ? 'text-yellow-600' : 'text-green-600'}`}>
                    {translationSummary?.fullyTranslatedCount ?? 0}/{translationSummary?.totalTemplates ?? 0}
                  </p>
                  <p className="text-sm text-gray-500">Templates vollstaendig uebersetzt</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Aktive Sprachen</p>
                  <p className="text-lg font-medium text-gray-900">
                    {languageConfig?.activeLanguages.map(l => l.code.toUpperCase()).join(', ') || '-'}
                  </p>
                </div>
              </div>
              {(translationSummary?.incompleteCount ?? 0) > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <FileText className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-yellow-800">
                        {translationSummary?.incompleteCount} Template(s) mit fehlenden Uebersetzungen
                      </p>
                      {translationSummary?.incompleteTemplates && translationSummary.incompleteTemplates.length > 0 && (
                        <ul className="text-sm text-yellow-700 mt-1 space-y-0.5">
                          {translationSummary.incompleteTemplates.slice(0, 3).map(t => (
                            <li key={t.templateId} className="truncate">
                              • {t.templateName}: {t.missingLanguages.map(l => l.toUpperCase()).join(', ')} fehlt
                            </li>
                          ))}
                          {translationSummary.incompleteTemplates.length > 3 && (
                            <li className="text-yellow-600">
                              ... und {translationSummary.incompleteTemplates.length - 3} weitere
                            </li>
                          )}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between pt-2">
                <div className={`text-xs px-2 py-1 rounded inline-block ${
                  (translationSummary?.incompleteCount ?? 0) === 0 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  Status: {(translationSummary?.incompleteCount ?? 0) === 0 ? 'Alle uebersetzt' : 'Uebersetzungen fehlen'}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/prompt-templates')}
                  className="flex items-center gap-1"
                >
                  Templates anzeigen
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* System Status */}
      <div className="grid grid-cols-1 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>System Status</CardTitle>
            <CardDescription>Aktueller Status der Platform-Komponenten</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-600 mb-2">Fehler beim Laden des System Status</p>
                <p className="text-sm text-gray-500">{error}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {systemStatus?.services.map((service) => {
                  const statusText = service.status === 'operational' ? 'Operativ' 
                    : service.status === 'maintenance' ? 'Wartung' 
                    : 'Ausgefallen';
                  
                  const textColor = service.status === 'operational' ? 'text-green-900' 
                    : service.status === 'maintenance' ? 'text-yellow-900' 
                    : 'text-red-900';
                  
                  const statusTextColor = service.status === 'operational' ? 'text-green-700' 
                    : service.status === 'maintenance' ? 'text-yellow-700' 
                    : 'text-red-700';
                  
                  const dotColor = service.status === 'operational' ? 'bg-green-500' 
                    : service.status === 'maintenance' ? 'bg-yellow-500' 
                    : 'bg-red-500';

                  return (
                    <div key={service.name} className={`flex items-center justify-between p-3 rounded-lg ${service.bgColor}`}>
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 ${dotColor} rounded-full`}></div>
                        <span className={`font-medium ${textColor}`}>{service.displayName}</span>
                      </div>
                      <span className={`text-sm ${statusTextColor}`}>{statusText}</span>
                    </div>
                  );
                })}
                {systemStatus?.services.length === 0 && (
                  <p className="text-center text-gray-500 py-4">Keine Services gefunden</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}